'use client'

import { SHOPIFY_ATTRIBUTES } from '@repo/shopify/attributes'
import { Attribute } from '@repo/shopify/types'
import { Button } from '@repo/ui/components/button'
import { Input } from '@repo/ui/components/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import { TextArea } from '@repo/ui/components/textarea'
import { Dispatch, FormEvent, FunctionComponent, PropsWithChildren, RefObject, SetStateAction, useState } from 'react'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { PageDto } from '../../../types/page.types'

interface Props {
  pageDto: PageDto
  form: RefObject<HTMLFormElement | null>
  setAdditionalAttributes: Dispatch<SetStateAction<Attribute[]>>
}

const ProductGiftCardSheet: FunctionComponent<PropsWithChildren<Props>> = ({ pageDto, form, setAdditionalAttributes }) => {
  const { isSheetOpen, closeSheet } = useSheet()
  const [senderName, setSenderName] = useState<string>('')
  const [recipientName, setRecipientName] = useState<string>('')
  const [recipientEmail, setRecipientEmail] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [message, setMessage] = useState<string>('')

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // The keys will be the line item's attribute names in Shopify.
    setAdditionalAttributes([
      { key: SHOPIFY_ATTRIBUTES.GIFT_CARD_SENDERS_NAME, value: senderName },
      { key: SHOPIFY_ATTRIBUTES.GIFT_CARD_RECIPIENTS_NAME, value: recipientName },
      { key: SHOPIFY_ATTRIBUTES.GIFT_CARD_RECIPIENTS_EMAIL, value: recipientEmail },
      { key: SHOPIFY_ATTRIBUTES.GIFT_CARD_DATE_OF_BIRTH, value: date },
      { key: SHOPIFY_ATTRIBUTES.GIFT_CARD_MESSAGE, value: message },
    ])

    closeSheet()
    form?.current?.requestSubmit()
  }

  return (
    <Sheet open={isSheetOpen(SHEET.GIFT_CARD)} onOpenChange={(open) => !open && closeSheet()}>
      <SheetContent side='right'>
        <SheetHeader>
          <SheetTitle>{pageDto.dictionary?.giftcard?.giftcard || 'Gift Card'}</SheetTitle>
        </SheetHeader>
        <SheetDescription>{pageDto.dictionary?.giftcard?.completeyourgiftcard || 'Complete your e-gift card.'}</SheetDescription>
        <form className='flex flex-col gap-4 overflow-y-auto p-3' onSubmit={handleSubmit}>
          <Input
            name='senderName'
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder={pageDto.dictionary?.giftcard?.sendername || `Sender's name *`}
            autoComplete='given-name'
            required={true}
          />
          <Input
            name='recipientName'
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder={pageDto.dictionary?.giftcard?.recipientname || `Recipient's name *`}
            required={true}
          />
          <Input
            name='recipientEmail'
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder={pageDto.dictionary?.giftcard?.recipientemail || `Recipient's email *`}
            required={true}
          />
          <Input type='date' value={date} onChange={(e) => setDate(e.target.value)} placeholder={pageDto.dictionary?.giftcard?.dateofbirth || `Date of birth (DD/MM/YYYY)`} />
          <TextArea
            name='message'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={pageDto.dictionary?.giftcard?.personalmessage || `Personal message`}
            rows={4}
          />
          <Button variant='defaultAuto' type='submit'>
            {pageDto.dictionary?.product?.addtobag || 'Add to bag'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default ProductGiftCardSheet
